import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
  timeout: 15000
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('chronos_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('chronos_token');
      localStorage.removeItem('chronos_username');
      // Only redirect if not already on login page
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    }
    if (error.code === 'ECONNABORTED') {
      error.message = 'Request timed out. Please try again.';
    }
    return Promise.reject(error);
  }
);

// Auth
export const login = (username, password) => api.post('/auth/login', { username, password });
export const verifyAuth = () => api.get('/auth/verify');

// Sender Accounts
export const getSenderAccounts = () => api.get('/sender-accounts');
export const createSenderAccount = (data) => api.post('/sender-accounts', data);
export const updateSenderAccount = (id, data) => api.put(`/sender-accounts/${id}`, data);
export const deleteSenderAccount = (id) => api.delete(`/sender-accounts/${id}`);
export const verifySenderAccount = (id) => api.post(`/sender-accounts/${id}/verify`);

// Schedules
export const getSchedules = () => api.get('/schedules');
export const getSchedule = (id) => api.get(`/schedules/${id}`);
export const createSchedule = (data) => api.post('/schedules', data);
export const updateSchedule = (id, data) => api.put(`/schedules/${id}`, data);
export const deleteSchedule = (id) => api.delete(`/schedules/${id}`);
export const pauseSchedule = (id) => api.post(`/schedules/${id}/pause`);
export const resumeSchedule = (id) => api.post(`/schedules/${id}/resume`);
export const sendNow = (id) => api.post(`/schedules/${id}/send-now`);

// Send Immediate (no schedule)
export const sendImmediate = (data) => api.post('/send-immediate', data);

// Recall
export const recallEmail = (logId) => api.post(`/recall/${logId}`);

// Logs
export const getEmailLogs = (limit = 100) => api.get(`/logs?limit=${limit}`);
export const getLogsBySchedule = (scheduleId) => api.get(`/logs/schedule/${scheduleId}`);

// Dashboard
export const getDashboardAll = () => api.get('/dashboard/all');
export const getDashboardStats = () => api.get('/dashboard/stats');
export const getUpcomingSchedules = () => api.get('/dashboard/upcoming');
export const getRecentLogs = () => api.get('/dashboard/recent-logs');

// File Uploads
export const uploadFile = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/uploads', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const deleteUpload = (filename) => api.delete(`/uploads/${filename}`);

// Timezones
export const getTimezones = () => api.get('/timezones');

export const uploadCsv = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/csv/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};

// AI Rephrase
export const rephraseEmail = (body_html, tone) => api.post('/ai/rephrase', { body_html, tone }, { timeout: 60000 });

// Analytics
export const getAnalyticsOverview = () => api.get('/analytics/overview');
export const getAnalyticsLogs = (limit = 200) => api.get(`/analytics/logs?limit=${limit}`);
export const getAnalyticsTimeline = (days = 30) => api.get(`/analytics/timeline?days=${days}`);

// Users
export const getUsers = () => api.get('/users');
export const createUser = (username, password) => api.post('/users', { username, password });
export const deleteUser = (userId) => api.delete(`/users/${userId}`);

export default api;
